import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';

interface UIState {
  // Active views
  activeView: 'lobby' | 'game' | 'tournament' | 'leaderboard' | null;

  // Modal/Dialog states
  showEndGameModal: boolean;
  showTournamentOverModal: boolean;
  showByeModal: boolean;
  showPoolTournamentOverModal: boolean;
  showPoolTournamentCancelledModal: boolean;

  // Abandonment warning data (non-blocking warning, not a modal)
  abandonmentWarning: {
    color: 'white' | 'black' | null;
    secondsRemaining: number;
    gameId: number | null;
  };

  // Loading states
  isSubmittingMove: boolean;
  isLoadingGame: boolean;
  isLoadingTournament: boolean;

  // Error states
  errorMessage: string | null;
  errorType: 'network' | 'game' | 'tournament' | null;

  // Feature flags for gradual migration
  useReduxForGames: boolean;
  useReduxForTournaments: boolean;
  useReduxForLeaderboard: boolean;

  // Notifications
  notifications: Array<{
    id: string;
    message: string;
    type: 'info' | 'warning' | 'error' | 'success';
    timestamp: number;
  }>;
}

const initialState: UIState = {
  activeView: null,
  showEndGameModal: false,
  showTournamentOverModal: false,
  showByeModal: false,
  showPoolTournamentOverModal: false,
  showPoolTournamentCancelledModal: false,
  abandonmentWarning: {
    color: null,
    secondsRemaining: 0,
    gameId: null
  },
  isSubmittingMove: false,
  isLoadingGame: false,
  isLoadingTournament: false,
  errorMessage: null,
  errorType: null,
  useReduxForGames: true, // Enable Redux for games by default
  useReduxForTournaments: true, // Enable Redux for tournaments
  useReduxForLeaderboard: true, // Enable Redux for leaderboard
  notifications: []
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // View management
    setActiveView: (state, action: PayloadAction<UIState['activeView']>) => {
      state.activeView = action.payload;
    },

    // Modal management
    showModal: (state, action: PayloadAction<'endGame' | 'tournamentOver' | 'bye' | 'poolTournamentOver' | 'poolTournamentCancelled'>) => {
      switch (action.payload) {
        case 'endGame':
          state.showEndGameModal = true;
          break;
        case 'tournamentOver':
          state.showTournamentOverModal = true;
          break;
        case 'bye':
          state.showByeModal = true;
          break;
        case 'poolTournamentOver':
          state.showPoolTournamentOverModal = true;
          break;
        case 'poolTournamentCancelled':
          state.showPoolTournamentCancelledModal = true;
          break;
      }
    },

    hideModal: (state, action: PayloadAction<'endGame' | 'tournamentOver' | 'bye' | 'poolTournamentOver' | 'poolTournamentCancelled'>) => {
      switch (action.payload) {
        case 'endGame':
          state.showEndGameModal = false;
          break;
        case 'tournamentOver':
          state.showTournamentOverModal = false;
          break;
        case 'bye':
          state.showByeModal = false;
          break;
        case 'poolTournamentOver':
          state.showPoolTournamentOverModal = false;
          break;
        case 'poolTournamentCancelled':
          state.showPoolTournamentCancelledModal = false;
          break;
      }
    },

    hideAllModals: (state) => {
      state.showEndGameModal = false;
      state.showTournamentOverModal = false;
      state.showByeModal = false;
      state.showPoolTournamentOverModal = false;
      state.showPoolTournamentCancelledModal = false;
    },

    // Abandonment warning
    setAbandonmentWarning: (state, action: PayloadAction<{ color: 'white' | 'black'; secondsRemaining: number; gameId: number }>) => {
      state.abandonmentWarning = {
        color: action.payload.color,
        secondsRemaining: action.payload.secondsRemaining,
        gameId: action.payload.gameId
      };
    },

    clearAbandonmentWarning: (state) => {
      state.abandonmentWarning = {
        color: null,
        secondsRemaining: 0,
        gameId: null
      };
    },

    // Loading states
    setSubmittingMove: (state, action: PayloadAction<boolean>) => {
      state.isSubmittingMove = action.payload;
    },

    setLoadingGame: (state, action: PayloadAction<boolean>) => {
      state.isLoadingGame = action.payload;
    },

    setLoadingTournament: (state, action: PayloadAction<boolean>) => {
      state.isLoadingTournament = action.payload;
    },

    // Error management
    setError: (state, action: PayloadAction<{ message: string; type: UIState['errorType'] }>) => {
      state.errorMessage = action.payload.message;
      state.errorType = action.payload.type;
    },

    clearError: (state) => {
      state.errorMessage = null;
      state.errorType = null;
    },

    // Feature flags
    setFeatureFlag: (state, action: PayloadAction<{ flag: keyof Pick<UIState, 'useReduxForGames' | 'useReduxForTournaments' | 'useReduxForLeaderboard'>; enabled: boolean }>) => {
      state[action.payload.flag] = action.payload.enabled;
    },

    // Notifications
    addNotification: (state, action: PayloadAction<Omit<UIState['notifications'][0], 'id' | 'timestamp'>>) => {
      const notification = {
        ...action.payload,
        id: `notif-${Date.now()}-${Math.random()}`,
        timestamp: Date.now()
      };
      state.notifications.push(notification);

      // Keep only last 10 notifications
      if (state.notifications.length > 10) {
        state.notifications = state.notifications.slice(-10);
      }
    },

    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload);
    },

    clearAllNotifications: (state) => {
      state.notifications = [];
    }
  }
});

export const {
  setActiveView,
  showModal,
  hideModal,
  hideAllModals,
  setSubmittingMove,
  setLoadingGame,
  setLoadingTournament,
  setError,
  clearError,
  setFeatureFlag,
  addNotification,
  removeNotification,
  clearAllNotifications,
  setAbandonmentWarning,
  clearAbandonmentWarning
} = uiSlice.actions;

// Selectors
export const selectActiveView = (state: RootState) => state.ui.activeView;
export const selectShowEndGameModal = (state: RootState) => state.ui.showEndGameModal;
export const selectShowTournamentOverModal = (state: RootState) => state.ui.showTournamentOverModal;
export const selectShowByeModal = (state: RootState) => state.ui.showByeModal;
export const selectShowPoolTournamentOverModal = (state: RootState) => state.ui.showPoolTournamentOverModal;
export const selectShowPoolTournamentCancelledModal = (state: RootState) => state.ui.showPoolTournamentCancelledModal;
export const selectAbandonmentWarning = (state: RootState) => state.ui.abandonmentWarning;
export const selectIsSubmittingMove = (state: RootState) => state.ui.isSubmittingMove;
export const selectIsLoadingGame = (state: RootState) => state.ui.isLoadingGame;
export const selectIsLoadingTournament = (state: RootState) => state.ui.isLoadingTournament;
export const selectErrorMessage = (state: RootState) => state.ui.errorMessage;
export const selectErrorType = (state: RootState) => state.ui.errorType;
export const selectUseReduxForGames = (state: RootState) => state.ui.useReduxForGames;
export const selectUseReduxForTournaments = (state: RootState) => state.ui.useReduxForTournaments;
export const selectUseReduxForLeaderboard = (state: RootState) => state.ui.useReduxForLeaderboard;
export const selectNotifications = (state: RootState) => state.ui.notifications;

export default uiSlice.reducer;
